import React, { useMemo, useRef, useState } from 'react';
import { Button } from '../../components/UI/Button';
import type { FormTheme, FormThemeFontFamily } from '../../types/form';
import {
  FORM_THEME_BACKGROUND_PRESETS,
  FORM_THEME_FONT_OPTIONS,
  FORM_THEME_PRIMARY_PRESETS,
  resolveFormTheme,
} from '../../types/form';

interface FormThemeEditorProps {
  theme: FormTheme;
  disabled?: boolean;
  isUploadingHeader?: boolean;
  hideTitle?: boolean;
  onChange: (theme: FormTheme) => void;
  onUploadHeader: (file: File) => Promise<void>;
  onRemoveHeader: () => Promise<void>;
  onRestoreDefault: () => void;
}

function ColorSwatch({
  color,
  selected,
  disabled,
  onSelect,
  label,
}: {
  color: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: (color: string) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={() => onSelect(color)}
      className={`relative h-8 w-8 rounded-full border-2 transition-transform focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
        selected
          ? 'border-gray-900 scale-110'
          : 'border-transparent hover:scale-105'
      }`}
      style={{ backgroundColor: color }}
    >
      {selected ? (
        <span className="absolute inset-0 flex items-center justify-center text-white drop-shadow">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      ) : null}
    </button>
  );
}

export const FormThemeEditor: React.FC<FormThemeEditorProps> = ({
  theme,
  disabled = false,
  isUploadingHeader = false,
  hideTitle = false,
  onChange,
  onUploadHeader,
  onRemoveHeader,
  onRestoreDefault,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const resolved = useMemo(() => resolveFormTheme(theme), [theme]);

  const patchTheme = (patch: Partial<FormTheme>) => {
    onChange(
      resolveFormTheme({
        ...resolved,
        ...patch,
        textSizes: {
          ...resolved.textSizes,
          ...(patch.textSizes ?? {}),
        },
      })
    );
  };

  const handleFileSelected = async (file: File | null) => {
    setUploadError(null);
    if (!file)
    {
      return;
    }

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type))
    {
      setUploadError('Use uma imagem JPG, PNG ou WebP.');
      return;
    }

    if (file.size > 2 * 1024 * 1024)
    {
      setUploadError('A imagem deve ter no máximo 2 MB.');
      return;
    }

    try
    {
      await onUploadHeader(file);
    }
    catch
    {
      setUploadError('Não foi possível enviar a imagem de cabeçalho.');
    }
  };

  return (
    <div className="space-y-6">
      {!hideTitle ? (
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Tema</h2>
            <p className="mt-1 text-xs text-gray-500">
              Personalize a aparência do formulário
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={onRestoreDefault}
          >
            Restaurar
          </Button>
        </div>
      ) : null}

      <section className="space-y-2">
        <h3 className="text-xs font-semibold text-gray-800">Cor primária</h3>
        <div className="flex flex-wrap items-center gap-2">
          {FORM_THEME_PRIMARY_PRESETS.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              selected={resolved.primaryColor === color}
              disabled={disabled}
              label={`Cor ${color}`}
              onSelect={(value) => patchTheme({ primaryColor: value })}
            />
          ))}
          <label className="ml-1 inline-flex items-center gap-2 text-sm text-gray-600">
            <span>Personalizada</span>
            <input
              type="color"
              disabled={disabled}
              value={resolved.primaryColor}
              onChange={(e) => patchTheme({ primaryColor: e.target.value })}
              className="h-8 w-10 cursor-pointer rounded border border-gray-200 bg-white p-0 disabled:cursor-not-allowed"
            />
          </label>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold text-gray-800">Plano de fundo</h3>
        <div className="flex flex-wrap items-center gap-2">
          {FORM_THEME_BACKGROUND_PRESETS.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              selected={resolved.backgroundColor === color}
              disabled={disabled}
              label={`Fundo ${color}`}
              onSelect={(value) => patchTheme({ backgroundColor: value })}
            />
          ))}
          <label className="ml-1 inline-flex items-center gap-2 text-xs text-gray-600">
            <span>Personalizado</span>
            <input
              type="color"
              disabled={disabled}
              value={resolved.backgroundColor}
              onChange={(e) => patchTheme({ backgroundColor: e.target.value })}
              className="h-8 w-10 cursor-pointer rounded border border-gray-200 bg-white p-0 disabled:cursor-not-allowed"
            />
          </label>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold text-gray-800">Fonte</h3>
        <select
          disabled={disabled}
          value={resolved.fontFamily}
          onChange={(e) =>
            patchTheme({
              fontFamily: e.target.value as FormThemeFontFamily,
            })
          }
          className="w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50"
        >
          {FORM_THEME_FONT_OPTIONS.map((font) => (
            <option key={font} value={font} style={{ fontFamily: font }}>
              {font}
            </option>
          ))}
        </select>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold text-gray-800">Tamanhos de texto</h3>
        <div className="space-y-3">
          {(
            [
              ['title', 'Título', 18, 36],
              ['section', 'Seção', 14, 28],
              ['question', 'Pergunta', 12, 22],
              ['body', 'Texto', 11, 18],
            ] as const
          ).map(([key, label, min, max]) => (
            <label key={key} className="block text-xs text-gray-700">
              <span className="mb-1 flex items-center justify-between">
                <span>{label}</span>
                <span className="text-xs text-gray-500">
                  {resolved.textSizes[key]}px
                </span>
              </span>
              <input
                type="range"
                min={min}
                max={max}
                step={1}
                disabled={disabled}
                value={resolved.textSizes[key]}
                onChange={(e) =>
                  patchTheme({
                    textSizes: {
                      ...resolved.textSizes,
                      [key]: Number(e.target.value),
                    },
                  })
                }
                className="w-full accent-blue-600 disabled:cursor-not-allowed"
              />
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold text-gray-800">Cabeçalho</h3>
        <p className="text-xs text-gray-500">
          Imagem exibida no topo do formulário público (JPG, PNG ou WebP, máx. 2
          MB).
        </p>

        {resolved.headerImageUrl ? (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <img
              src={resolved.headerImageUrl}
              alt="Cabeçalho do formulário"
              className="h-36 w-full object-cover"
            />
          </div>
        ) : (
          <div className="flex h-28 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500">
            Nenhuma imagem selecionada
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={disabled || isUploadingHeader}
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              void handleFileSelected(file);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled || isUploadingHeader}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploadingHeader ? 'Enviando...' : 'Escolher imagem'}
          </Button>
          {resolved.headerImageKey || resolved.headerImageUrl ? (
            <Button
              type="button"
              variant="dangerOutline"
              size="sm"
              disabled={disabled || isUploadingHeader}
              onClick={() => {
                void onRemoveHeader();
              }}
            >
              Remover
            </Button>
          ) : null}
        </div>
        {uploadError ? (
          <p className="text-sm text-red-600">{uploadError}</p>
        ) : null}
      </section>
    </div>
  );
};

export default FormThemeEditor;
